import "./App.css";
import * as THREE from "three";
import React, { useRef, Suspense, useEffect, useState, lazy, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import {
  Reflector,
  Html,
  useTexture,
  useGLTF,
  useProgress,
  useCursor,
  useAnimations,
  Text,
} from "@react-three/drei";
import useTransition from "./useTransition";
import { VideoText } from "./VideoText";

const Carla = React.memo((props) => {
  const { scene, animations } = useGLTF("/scene.glb");
  const [hovered, setActive] = useState(false);
  const [index, setIndex] = useState(4);
  const { ref, actions, names } = useAnimations(animations);

  useEffect(() => {
    actions[names[index]].reset().fadeIn(0.5).play();
    return () => actions[names[index]].fadeOut(0.5);
  }, [index, actions, names]);

  useCursor(hovered);
  return (
    <primitive
      onClick={() => setIndex((index + 1) % names.length)}
      ref={ref}
      onPointerOver={() => setActive(true)}
      onPointerOut={() => setActive(false)}
      object={scene}
      {...props}
    />
  );
});

const Ground = React.memo(() => {
  const [floor, normal] = useTexture([
    "/SurfaceImperfections003_1K_var1.jpg",
    "/SurfaceImperfections003_1K_Normal.jpg",
  ]);

  return (
    <Reflector
      resolution={512}
      args={[10, 10]}
      mirror={0.4}
      mixBlur={8}
      mixStrength={1}
      rotation={[-Math.PI / 2, 0, Math.PI / 2]}
      blur={[400, 100]}
    >
      {(Material, props) => (
        <Material
          color="#a0a0a0"
          metalness={0.4}
          roughnessMap={floor}
          normalMap={normal}
          normalScale={[1, 1]}
          {...props}
        />
      )}
    </Reflector>
  );
});



const ButtonLink = React.memo(({ ...props }) => {
  const mesh = useRef();
  const text = useRef();
  const [hovered, setHover] = useState(false);
  const [active, setActive] = useState(false);
  const url = "https://www.linkedin.com/in/jacob-yanov/";

  const handleClick = () => {
    window.open(url, "_blank");
    setActive(!active);
  };

  useCursor(hovered);

  useFrame(() => {
    if (hovered) {
      text.current.position.y = Math.sin(Date.now() / 500) * 0.02 + 0.2;
    } else {
      text.current.position.y = 0.09;
    }
  });

  return (
    <mesh
      ref={mesh}
      {...props}
      onClick={handleClick}
      onPointerOver={() => setHover(true)}
      onPointerOut={() => setHover(false)}
      position={[0, active ? -0.03 : 0, 0]}
    >
      <cylinderBufferGeometry args={[0.14, 0.14, 0.14, 32]} />
      <meshPhysicalMaterial
        envMapIntensity={0.4}
        clearcoat={0.8}
        clearcoatRoughness={0}
        roughness={1}
        metalness={0}
        color={hovered ? "orange" : "black"}
      />
      <Text
        ref={text}
        anchorX="center"
        anchorY="middle"
        position={[0, 0.09, 0]}
        rotation={[-Math.PI / 4, 0, 0]}
        fontSize={hovered ? 0.2 : 0.045}
        color={hovered ? "yellow" : "white"}
        style={{ cursor: "pointer" }}
        fontWeight="bold"
      >
        Contact Me!
      </Text>
    </mesh>
  );
});

const Loadera = ({ ready, clicked, setClicked }) => {
  const { active, progress } = useProgress();
  const style = useTransition(active, {
    from: { opacity: 1, transform: "translateY(20px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(-20px)" },
    duration: 1000,
    onRest: () => setClicked(true),
  });

  useEffect(() => {
    if (progress === 100) {
      setClicked(true);
    }
  }, [progress]);

  return (
    active && (
      <div className="loading" style={{ ...style, transition: "all 1000ms ease" }}>
        <div>Spread the Pawsitivity! 🐾❤️</div>
        <br />
        <div className="loading-bar-container">
          <div className="loading-bar" style={{ width: `${progress.toFixed(2)}%` }}>
            <span className="loading-data">{`${progress.toFixed(2)}%`}</span>
          </div>
        </div>
      </div>
    )
  );
};


const Intro = () => {
  const [vec] = useState(() => new THREE.Vector3())
  return useFrame((state) => {
    state.camera.position.lerp(vec.set(state.mouse.x * 5, 3 + state.mouse.y * 2, 14), 0.05)
    state.camera.lookAt(0, 0, 0)
  })
}

function App() {
  const [clicked, setClicked] = useState(false);
  const [ready, setReady] = useState(false);
  const [color, setColor] = useState("#0c8cbf");
  const store = useMemo(() => ({ clicked, setClicked, ready, setReady }), [clicked, ready]);

  useEffect(() => {
    if (clicked) {
      setReady(true);
    }
  }, [clicked]);

  return (
    <>
      <Canvas camera={{ position: [0, 3, 20], fov: 15 }}>
        <Suspense fallback={<Html center>Loading...</Html>}>
          <group position={[0, -1, 0]}>
            <Carla
              rotation={[0, Math.PI - 0.4, 0]}
              position={[-0.8, 0, 0.9]}
              scale={[0.0056, 0.0056, 0.0056]}
            />
            <VideoText
              ready={ready}
              {...store}
              position={[0, 1.3, -2]}
              onChange={(c) => setColor(c)}
            />
            <ButtonLink position={[0, 0.01, 0.8]} />
            <Ground />
          </group>
        </Suspense>
        <ambientLight intensity={1} />
        <spotLight position={[0, 10, 0]} intensity={0.8} color={color} />
        <directionalLight position={[1, 0, 2]} intensity={0.7} color={color} />
        <Intro start={ready && clicked} set={setReady} />
      </Canvas>
      <Loadera {...store} />
    </>
  );
}

export default App;
